import { ActionReducerMapBuilder, PayloadAction } from "@reduxjs/toolkit";
import {
  MemberAllOrderResponseType,
  MemberOrderRebateResponseType,
  MemberOrderResponseType,
  MemberOrderRewardResponseType,
  MemberPromotionResponse,
} from "api/orders/type";
import { NoInfer } from "react-redux";
import {
  MemberAllTransactionSummary,
  MemberOrderStateType,
  MemberOrderType,
  RewardOrderType,
} from "types/orders";

import {
  getMemberAllHistoryOrders,
  getMemberDepositOrders,
  getMemberPromotions,
  getMemberRebateOrders,
  getMemberRewardOrders,
  getMemberWithdrawalOrders,
} from "./orders.thunk";

const normalizeOrderList = (
  orders: MemberOrderType[] | null | undefined
): MemberOrderType[] => (Array.isArray(orders) ? orders : []);

const getOrderKey = (item: Partial<MemberOrderType>) =>
  item.transaction_code ||
  item.transaction_id ||
  [
    item.create_date,
    item.update_date,
    item.amount,
    item.method,
    item.promotion,
    item.status,
  ]
    .filter(Boolean)
    .join("-");

const mergeUniqueOrders = (
  current: MemberOrderType[] | null | undefined,
  incoming: MemberOrderType[] | null | undefined
) =>
  Array.from(
    new Map(
      [...normalizeOrderList(current), ...normalizeOrderList(incoming)].map(
        (item) => [getOrderKey(item), item]
      )
    ).values()
  );

const getRewardKey = (item: RewardOrderType) =>
  item.transactionid || [item.date, item.amount, item.method].join("-");

const mergeUniqueRewards = (
  current: RewardOrderType[] | null,
  incoming: RewardOrderType[]
) =>
  Array.from(
    new Map(
      [...(current || []), ...incoming].map((item) => [
        getRewardKey(item),
        item,
      ])
    ).values()
  );

const getErrorMessage = (
  payload: unknown,
  fallback = "Unable to load transaction history"
) => {
  if (typeof payload === "string") {
    return payload;
  }

  if (
    payload &&
    typeof payload === "object" &&
    "message" in payload &&
    typeof payload.message === "string"
  ) {
    return payload.message;
  }

  return fallback;
};

const extractAllSummary = (
  payload: MemberAllOrderResponseType
): MemberAllTransactionSummary | null => payload.summary;

export const extraReducers = (
  builder: ActionReducerMapBuilder<NoInfer<MemberOrderStateType>>
) => {
  builder
    .addCase(
      getMemberAllHistoryOrders.pending,
      (state: MemberOrderStateType) => {
        state.memberOrders.all.isLoading = true;
        state.memberOrders.all.error = null;
      }
    )
    .addCase(
      getMemberAllHistoryOrders.fulfilled,
      (
        state: MemberOrderStateType,
        action: PayloadAction<MemberAllOrderResponseType>
      ) => {
        state.memberOrders.all.isLoading = false;
        state.memberOrders.all.error = null;
        state.memberOrders.all.data = mergeUniqueOrders(
          state.memberOrders.all.data,
          action.payload.data
        );
        state.memberOrders.all.total = action.payload.totalCount;
        state.memberOrders.all.page = action.payload.currentPage;
        state.memberOrders.all.pageSize = action.payload.pageSize;
        state.memberOrders.all.summary = extractAllSummary(action.payload);
      }
    )
    .addCase(getMemberAllHistoryOrders.rejected, (state, action) => {
      state.memberOrders.all.isLoading = false;
      state.memberOrders.all.error = getErrorMessage(
        action.payload,
        action.error.message
      );
    })
    .addCase(getMemberDepositOrders.pending, (state: MemberOrderStateType) => {
      state.memberOrders.deposit.isLoading = true;
      state.memberOrders.deposit.error = null;
    })
    .addCase(
      getMemberDepositOrders.fulfilled,
      (
        state: MemberOrderStateType,
        action: PayloadAction<MemberOrderResponseType>
      ) => {
        state.memberOrders.deposit.isLoading = false;
        state.memberOrders.deposit.error = null;
        state.memberOrders.deposit.data = mergeUniqueOrders(
          state.memberOrders.deposit.data,
          action.payload.data
        );
        state.memberOrders.deposit.total = action.payload.totalCount;
        state.memberOrders.deposit.page = action.payload.currentPage;
        state.memberOrders.deposit.pageSize = action.payload.pageSize;
      }
    )
    .addCase(getMemberDepositOrders.rejected, (state, action) => {
      state.memberOrders.deposit.isLoading = false;
      state.memberOrders.deposit.error = getErrorMessage(
        action.payload,
        action.error.message
      );
    })
    .addCase(
      getMemberWithdrawalOrders.pending,
      (state: MemberOrderStateType) => {
        state.memberOrders.withdrawal.isLoading = true;
        state.memberOrders.withdrawal.error = null;
      }
    )
    .addCase(
      getMemberWithdrawalOrders.fulfilled,
      (
        state: MemberOrderStateType,
        action: PayloadAction<MemberOrderResponseType>
      ) => {
        state.memberOrders.withdrawal.isLoading = false;
        state.memberOrders.withdrawal.error = null;
        state.memberOrders.withdrawal.data = mergeUniqueOrders(
          state.memberOrders.withdrawal.data,
          action.payload.data
        );
        state.memberOrders.withdrawal.total = action.payload.totalCount;
        state.memberOrders.withdrawal.page = action.payload.currentPage;
        state.memberOrders.withdrawal.pageSize = action.payload.pageSize;
      }
    )
    .addCase(getMemberWithdrawalOrders.rejected, (state, action) => {
      state.memberOrders.withdrawal.isLoading = false;
      state.memberOrders.withdrawal.error = getErrorMessage(
        action.payload,
        action.error.message
      );
    })
    .addCase(getMemberRebateOrders.pending, (state: MemberOrderStateType) => {
      state.memberOrders.rebate.isLoading = true;
      state.memberOrders.rebate.error = null;
    })
    .addCase(
      getMemberRebateOrders.fulfilled,
      (
        state: MemberOrderStateType,
        action: PayloadAction<MemberOrderRebateResponseType>
      ) => {
        state.memberOrders.rebate.isLoading = false;
        state.memberOrders.rebate.error = null;
        state.memberOrders.rebate.data = Array.isArray(
          state.memberOrders.rebate.data
        )
          ? [...state.memberOrders.rebate.data, ...action.payload.data]
          : action.payload.data;
        state.memberOrders.rebate.total = action.payload.totalCount;
      }
    )
    .addCase(getMemberRebateOrders.rejected, (state, action) => {
      state.memberOrders.rebate.isLoading = false;
      state.memberOrders.rebate.error = getErrorMessage(
        action.payload,
        action.error.message
      );
    })
    .addCase(getMemberRewardOrders.pending, (state: MemberOrderStateType) => {
      state.memberOrders.reward.isLoading = true;
      state.memberOrders.reward.error = null;
    })
    .addCase(
      getMemberRewardOrders.fulfilled,
      (
        state: MemberOrderStateType,
        action: PayloadAction<MemberOrderRewardResponseType>
      ) => {
        state.memberOrders.reward.isLoading = false;
        state.memberOrders.reward.error = null;
        state.memberOrders.reward.data = mergeUniqueRewards(
          state.memberOrders.reward.data,
          action.payload.data
        );
        state.memberOrders.reward.total = action.payload.totalCount;
        state.memberOrders.reward.page = action.payload.currentPage;
        state.memberOrders.reward.pageSize = action.payload.pageSize;
      }
    )
    .addCase(getMemberRewardOrders.rejected, (state, action) => {
      state.memberOrders.reward.isLoading = false;
      state.memberOrders.reward.error = getErrorMessage(
        action.payload,
        action.error.message
      );
    })
    .addCase(getMemberPromotions.pending, (state: MemberOrderStateType) => {
      state.memberPromotions.isLoading = true;
      state.memberPromotions.error = null;
    })
    .addCase(
      getMemberPromotions.fulfilled,
      (
        state: MemberOrderStateType,
        action: PayloadAction<MemberPromotionResponse>
      ) => {
        state.memberPromotions.isLoading = false;
        state.memberPromotions.error = null;
        state.memberPromotions.data = {
          list: action.payload.data.list,
          claim_reward: action.payload.data.claim_reward,
        };
        state.memberPromotions.total = action.payload.totalCount;
      }
    )
    .addCase(getMemberPromotions.rejected, (state, action) => {
      state.memberPromotions.isLoading = false;
      state.memberPromotions.error = getErrorMessage(
        action.payload,
        action.error.message
      );
    });
};
